/*
 * Feature Sections — animated scene illustrations for the "Mission Deck"
 * bento grid. Rendered inside <LazyMotion features={domAnimation}> by
 * FeatureSections.tsx — always use `m.*` components here, never `motion.*`.
 *
 * Every scene receives { active, reduced }: looping animations must be
 * gated on `active` (card in view), and `reduced` swaps entrances to
 * opacity-only fades for prefers-reduced-motion users.
 */
import { Variants, m } from 'framer-motion';
import React from 'react';

export const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

export const CHECK_D = 'M2.5 6.5L5 9L9.5 3.5';

export interface SceneProps {
  active: boolean;
  reduced: boolean;
}

const fadeVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.4 } },
};

/* Reduced-motion users get opacity-only fades everywhere. */
const pick = (reduced: boolean, full: Variants): Variants => (reduced ? fadeVariants : full);

const riseVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT } },
};

/* ═══ Scene 1 — Multi-agent orbit (hero) ═══ */

const ringlineVariants: Variants = {
  hidden: { opacity: 0, scale: 0.88 },
  show: { opacity: 0.65, scale: 1, transition: { duration: 0.7, ease: EASE_OUT } },
  alive: { opacity: 1 },
};

const coreVariants: Variants = {
  hidden: { opacity: 0, scale: 0.7 },
  show: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 200, damping: 18 } },
  alive: { scale: 1.08, transition: { type: 'spring', stiffness: 220, damping: 18 } },
};

const OrbitRing: React.FC<{
  cls: string;
  duration: number;
  reverse?: boolean;
  labels: string[];
  active: boolean;
}> = ({ cls, labels }) => (
  <m.div
    className={`fs-orbit__rotor ${cls}`}
  >
    {labels.map((label, i) => (
      <div key={label} className={`fs-orbit__pos fs-orbit__pos--${i + 1}`}>
        <m.div
          className="fs-orbit__pill"
        >
          <span className="fs-orbit__pilldot" aria-hidden="true" />
          {label}
        </m.div>
      </div>
    ))}
  </m.div>
);

export const SceneAgents: React.FC<SceneProps> = ({ active, reduced }) => (
  <div className="fs-orbit">
    <div className="fs-orbit__stage">
      <m.div className="fs-orbit__ringline fs-orbit__ringline--inner" variants={pick(reduced, ringlineVariants)} />
      <m.div className="fs-orbit__ringline fs-orbit__ringline--outer" variants={pick(reduced, ringlineVariants)} />
      <m.div className="fs-orbit__core" variants={pick(reduced, coreVariants)}>8080</m.div>
      <OrbitRing cls="fs-orbit__rotor--inner" duration={46} labels={['Tech Lead', 'PM', 'Designer']} active={active} />
      <OrbitRing
        cls="fs-orbit__rotor--outer"
        duration={64}
        reverse
        labels={['Frontend', 'Backend', 'DevOps']}
        active={active}
      />
    </div>
  </div>
);

/* ═══ Scene 2 — Architecture graph ═══ */

const ARCH_NODES = [
  { x: 120, y: 18, w: 100, label: 'api-gateway', main: true },
  { x: 20, y: 104, w: 84, label: 'user-svc' },
  { x: 128, y: 104, w: 84, label: 'agent-svc' },
  { x: 236, y: 104, w: 84, label: 'task-svc' },
  { x: 52, y: 196, w: 96, label: 'mariadb' },
  { x: 196, y: 196, w: 88, label: 'redis' },
];

const ARCH_PATHS = [
  'M170 48 C170 76 62 76 62 104',
  'M170 48 L170 104',
  'M170 48 C170 76 278 76 278 104',
  'M62 132 C62 164 100 164 100 196',
  'M170 132 C170 164 240 164 240 196',
  'M278 132 C278 164 240 164 240 196',
];

const archWiresVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const wireVariants: Variants = {
  hidden: { pathLength: 0, opacity: 0 },
  show: { pathLength: 1, opacity: 0.85, transition: { duration: 0.9, ease: 'easeOut' } },
  alive: { opacity: 1 },
};

const archNodeVariants: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  show: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: 0.55 + i * 0.08, duration: 0.4, ease: EASE_OUT },
  }),
};

export const SceneArchitect: React.FC<SceneProps> = ({ active, reduced }) => (
  <div className="fs-arch">
    <svg viewBox="0 0 340 236" fill="none" focusable="false" role="presentation">
      <m.g variants={pick(reduced, archWiresVariants)}>
        {ARCH_PATHS.map((d) => (
          <m.path key={d} className="fs-arch__wire" d={d} variants={pick(reduced, wireVariants)} />
        ))}
      </m.g>
      {ARCH_NODES.map((n, i) => (
        <m.g
          key={n.label}
          className={`fs-arch__node${n.main ? ' fs-arch__node--main' : ''}`}
          variants={pick(reduced, archNodeVariants)}
          custom={i}
        >
          <rect x={n.x} y={n.y} width={n.w} height={28} rx={6} />
          <text x={n.x + n.w / 2} y={n.y + 18}>{n.label}</text>
        </m.g>
      ))}
      <m.circle
        className="fs-arch__packet"
        cx="170"
        cy="54"
        r="3.5"
        initial={{ cy: 54, opacity: 0 }}
        animate={active ? { cy: [54, 100], opacity: [0, 1, 0] } : { cy: 54, opacity: 0 }}
        transition={{ duration: 1.8, repeat: 2, repeatDelay: 4.5, ease: 'easeInOut' }}
      />
    </svg>
  </div>
);

/* ═══ Scene 3 — Kanban ═══ */

const KAN_COLS = [
  { name: 'Backlog', tickets: 2, spaced: false },
  { name: 'In Progress', tickets: 0, spaced: false },
  { name: 'Done', tickets: 1, spaced: true },
];

const KanbanColumn: React.FC<{ col: (typeof KAN_COLS)[number]; reduced: boolean }> = ({ col, reduced }) => (
  <div className="fs-kan__col">
    <m.span className="fs-kan__head" variants={pick(reduced, riseVariants)}>{col.name}</m.span>
    {Array.from({ length: col.tickets }).map((_, i) => (
      <m.div
        key={i}
        className={`fs-kan__ticket${col.spaced ? ' fs-kan__ticket--slot2' : ''}`}
        variants={pick(reduced, riseVariants)}
      >
        <span className="fs-kan__bar fs-kan__bar--wide" />
        <span className="fs-kan__bar" />
      </m.div>
    ))}
  </div>
);

export const ScenePM: React.FC<SceneProps> = ({ active, reduced }) => (
  <div className="fs-kan">
    {KAN_COLS.map((col) => (
      <KanbanColumn key={col.name} col={col} reduced={reduced} />
    ))}
    <m.div
      className="fs-kan__ticket fs-kan__mover"
      animate={active ? { x: [0, 0, 104, 104], opacity: [0, 1, 1, 0] } : undefined}
      transition={{ duration: 10, times: [0, 0.06, 0.3, 0.95], repeat: 1, ease: 'easeInOut' }}
    >
      <span className="fs-kan__bar fs-kan__bar--wide" />
      <span className="fs-kan__bar" />
    </m.div>
    <m.div
      className="fs-kan__pop"
      animate={active ? { scale: [0, 0, 1, 1], opacity: [0, 0, 1, 0] } : undefined}
      transition={{ duration: 10, times: [0, 0.3, 0.36, 1], repeat: 1, ease: 'easeOut' }}
    >
      <svg viewBox="0 0 12 12" fill="none" focusable="false">
        <path d={CHECK_D} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </m.div>
  </div>
);

/* ═══ Scene 4 — Kubernetes deploy (hero) ═══ */

const SPOKES = Array.from({ length: 7 }, (_, i) => {
  const a = (Math.PI * 2 * i) / 7 - Math.PI / 2;

  return {
    x1: 100 + 28 * Math.cos(a),
    y1: 100 + 28 * Math.sin(a),
    x2: 100 + 70 * Math.cos(a),
    y2: 100 + 70 * Math.sin(a),
  };
});

const rolloutVariants: Variants = {
  hidden: { pathLength: 0, opacity: 0 },
  show: { pathLength: 1, opacity: 0.6, transition: { duration: 1.1, ease: 'easeOut', delay: 0.2 } },
};

const PodGrid: React.FC = () => (
  <div className="fs-k8s__pods">
    {Array.from({ length: 9 }).map((_, i) => (
      <div key={i} className="fs-pod" />
    ))}
  </div>
);

export const SceneK8s: React.FC<SceneProps> = ({ reduced }) => (
  <div className="fs-k8s">
    <svg className="fs-k8s__wheel" viewBox="0 0 200 200" fill="none" focusable="false" role="presentation">
      <m.circle className="fs-k8s__rollout" cx="100" cy="100" r="92" variants={pick(reduced, rolloutVariants)} />
      <m.g
        className="fs-k8s__rotor"
        style={{ originX: 0.5, originY: 0.5 }}
      >
        <circle className="fs-k8s__rim" cx="100" cy="100" r="72" />
        {SPOKES.map((s, i) => (
          <line key={i} className="fs-k8s__spoke" x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} />
        ))}
        {SPOKES.map((s, i) => (
          <circle key={`p${i}`} className="fs-k8s__port" cx={s.x2} cy={s.y2} r={3} />
        ))}
        <circle className="fs-k8s__hub" cx="100" cy="100" r="26" />
        <circle className="fs-k8s__hubdot" cx="100" cy="100" r="9" />
      </m.g>
    </svg>
    <PodGrid />
  </div>
);

/* ═══ Scene 5 — Visual testing ═══ */

const TestBadges: React.FC<{ reduced: boolean }> = ({ reduced }) => (
  <div className="fs-test__badges">
    <m.span className="fs-test__badge fs-test__badge--pass" variants={pick(reduced, riseVariants)}>
      <svg viewBox="0 0 12 12" fill="none" focusable="false">
        <path d={CHECK_D} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      284 passed
    </m.span>
    <m.span className="fs-test__badge" variants={pick(reduced, riseVariants)}>0 failed</m.span>
  </div>
);

export const SceneTesting: React.FC<SceneProps> = ({ active, reduced }) => (
  <div className="fs-test">
    <m.div className="fs-browser" variants={pick(reduced, riseVariants)}>
      <div className="fs-browser__bar">
        <span />
        <span />
        <span className="fs-browser__dot--hot" />
        <div className="fs-browser__url">app.8080.ai/preview</div>
      </div>
      <div className="fs-browser__page">
        <span className="fs-skel fs-skel--1" />
        <span className="fs-skel fs-skel--2" />
        <span className="fs-skel fs-skel--3" />
        <span className="fs-skel fs-skel--block" />
        <m.div
          className="fs-test__ripple"
          animate={active ? { scale: [0, 1.8], opacity: [0.55, 0] } : undefined}
          transition={{ duration: 1.1, repeat: 2, repeatDelay: 7.9, delay: 2.4, ease: 'easeOut' }}
        />
        <m.div
          className="fs-test__cursor"
          animate={active ? { x: [10, 118, 118, 44, 10], y: [14, 36, 36, 84, 14] } : undefined}
          transition={{ duration: 9, times: [0, 0.26, 0.42, 0.72, 1], repeat: 1, ease: 'easeInOut' }}
        >
          <svg viewBox="0 0 16 16" fill="none" focusable="false">
            <path d="M3 2L13 8.5L8.5 9.5L6.5 14L3 2Z" fill="currentColor" />
          </svg>
        </m.div>
      </div>
    </m.div>
    <TestBadges reduced={reduced} />
  </div>
);

/* ═══ Scene 6 — Code editor ═══ */

const EDITOR_LINES: { indent: number; segs: { w: number; accent?: boolean }[] }[] = [
  { indent: 0, segs: [{ w: 34, accent: true }, { w: 58 }] },
  { indent: 1, segs: [{ w: 46 }, { w: 28, accent: true }] },
  { indent: 1, segs: [{ w: 72 }] },
  { indent: 2, segs: [{ w: 38, accent: true }, { w: 44 }] },
  { indent: 2, segs: [{ w: 56 }] },
  { indent: 1, segs: [{ w: 30 }] },
  { indent: 0, segs: [{ w: 22, accent: true }] },
];

const editorLineVariants: Variants = {
  hidden: { scaleX: 0, opacity: 0 },
  show: (i: number) => ({
    scaleX: 1,
    opacity: 1,
    transition: { delay: 0.25 + i * 0.07, duration: 0.35, ease: 'easeOut' },
  }),
};

const tablineVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.5, delay: 0.3 } },
};

const EditorLine: React.FC<{ line: (typeof EDITOR_LINES)[number]; index: number; reduced: boolean }> = (props) => (
  <m.div
    className="fs-editor__line"
    style={{ paddingLeft: props.line.indent * 14 }}
    variants={pick(props.reduced, editorLineVariants)}
    custom={props.index}
  >
    {props.line.segs.map((seg, j) => (
      <span
        key={j}
        className={`fs-editor__seg${seg.accent ? ' fs-editor__seg--accent' : ''}`}
        style={{ width: seg.w }}
      />
    ))}
  </m.div>
);

export const SceneEditor: React.FC<SceneProps> = ({ reduced }) => (
  <div className="fs-editor">
    <div className="fs-editor__chrome">
      <div className="fs-editor__tab fs-editor__tab--active">
        app.tsx
        <m.span className="fs-editor__tabline" variants={pick(reduced, tablineVariants)} />
      </div>
      <div className="fs-editor__tab">agents.ts</div>
    </div>
    <div className="fs-editor__split">
      <div className="fs-editor__side">
        {Array.from({ length: 4 }).map((_, i) => (
          <span key={i} />
        ))}
        <span className="fs-editor__diag" />
      </div>
      <div className="fs-editor__code">
        {EDITOR_LINES.map((line, i) => (
          <EditorLine key={i} line={line} index={i} reduced={reduced} />
        ))}
        <span className="fs-editor__caret" aria-hidden="true" />
      </div>
    </div>
  </div>
);

/* ═══ Scene 7 — Auto-scaling bars ═══ */

const SCALE_BARS = [34, 48, 42, 62, 80, 102, 126];

const scaleGroupVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.25 } },
};

const scaleBarVariants: Variants = {
  hidden: { scaleY: 0 },
  show: { scaleY: 1, transition: { type: 'spring', stiffness: 140, damping: 13 } },
  alive: (i: number) => ({
    scaleY: [1, 1.08, 1],
    transition: { duration: 0.5, delay: i * 0.04 },
  }),
};

const scaleChipVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { delay: 0.85, duration: 0.4, ease: EASE_OUT } },
};

export const SceneScale: React.FC<SceneProps> = ({ reduced }) => (
  <div className="fs-scale">
    <div className="fs-scale__rules" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
    <m.div className="fs-scale__bars" variants={pick(reduced, scaleGroupVariants)}>
      {SCALE_BARS.map((h, i) => (
        <m.div
          key={i}
          className="fs-scale__bar"
          style={{ height: h }}
          variants={pick(reduced, scaleBarVariants)}
          custom={i}
        />
      ))}
    </m.div>
    <m.div className="fs-scale__chip" variants={pick(reduced, scaleChipVariants)}>+240% this week</m.div>
  </div>
);
